import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_teamMutation,
  useGET_teamByIdQuery,
  usePOST_teamMutation,
  usePUT_teamMutation,
} from 'redux/api/teamsApi'
import { useGET_groupCourseSelectQuery } from 'redux/api/groupCoursesApi'

import { ActionType, ITeam } from 'types'
import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/teamModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'
import useCrudData from 'utils/customHooks/useCrudData'
import InputField from 'components/atoms/fields/InputField'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const TeamForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug ='/teams'
  const editSlug = `/teams/edit/${id}`
  const editPrefix = '/teams/edit/'

  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    editPrefix,
    entityName: `${t('common.group-schedule')}`,
    entityId: id,
    getEntityQuery: useGET_teamByIdQuery,
    createEntityMutation: usePOST_teamMutation,
    editEntityMutation: usePUT_teamMutation,
    deleteEntityMutation: useDELETE_teamMutation
  })

  const {
    title,
    group_course
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...entity,
        group_course: {id: entity.group_course.id, title: `${entity.group_course.group} / ${entity.group_course.course}`}
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: ITeam = {
          ...values,
          group_course_id: values.group_course.id,
        }
        handleSubmit(action, id, data);
      }}
    >
      {
        ({ values, setFieldValue, errors }) => 
        <Form>
          <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
            <Grid item xs={12} md={6}>
              <SelectField
                  renderChildren={false}
                  fieldProps={{ ...group_course }}
                  apiCall={useGET_groupCourseSelectQuery}
                  disabled={viewOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputField fieldProps={{ ...title }} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }} className="transparent-background">
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>
                {action === 'view' && 
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(editSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.edit')}`}
                    </Button>
                  </Box>
                }
                {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    sx={{ width: '10rem' }}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Form>
      }
    </Formik>
  )
}
export default TeamForm
